import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../assets/Icons";
import BeneficiaryAccountViewAll from "../../../containers/Accounts/BeneficiaryAccount/BeneficiaryAccountViewAll";
import ReimbursementAccountViewAll from "../../../containers/Accounts/ReimbursementAccount/AccountViewAll";
import TaxationAccountViewAll from "../../../containers/Accounts/TaxationAccount/TaxationAccountViewAll";
import FieldForceAccountViewAll from "../../../containers/FieldForceAccount/FieldForceAccountViewAll";
import LargePopupRedirect from "../CommonPopups/CommonLargePopupRedirect";
import SmallPopup from "../CommonSmallPopup";
import SVGComponent from "../SVGComponent";

/**
 * ActiveInactiveAssociatePopup
 *
 * A reusable component that displays associated account information
 * based on different flows (PAN, Bank, Existing, Not Found).
 * It also renders a popup with account details and allows toggling
 * between active and inactive accounts.
 *
 * @param {Object} props - Component props
 *
 * @param {"Pan"|"Bank"|"Existing"|"notFoundPan"|"notFoundBank"} props.flow
 * Determines which UI state to render:
 * - "Pan": Show PAN-based associated accounts
 * - "Bank": Show Bank-based associated accounts
 * - "Existing": Existing account found UI
 * - "notFound": No associated accounts UI
 *
 * @param {string} [props.pan]
 * PAN number used for fetching associated accounts
 *
 * @param {string} [props.ifsc]
 * IFSC code of the bank account
 *
 * @param {string} [props.bank]
 * Bank account number
 *
 * @param {"active"|"inactive"} props.accActiveInactiveState
 * Current tab state indicating whether active or inactive accounts are shown
 *
 * @param {(state: "active"|"inactive") => void} props.setAccActiveInactiveState
 * Function to update active/inactive tab state
 *
 * @param {string} props.currentAssociate
 * Currently selected associate type from dropdown
 * उदाहरण: "Field force account", "Reimbursement account"
 *
 * @param {(value: string) => void} props.setCurrentAssociate
 * Function to update selected associate type
 *
 * @param {boolean} props.openAssociatePopup
 * Controls visibility of the popup
 *
 * @param {(value: boolean) => void} props.setOpenAssociatePopup
 * Function to open/close the popup
 *
 * @param {string[]} props.dropDownOptions
 * List of dropdown options for selecting associate type
 *
 * @returns {JSX.Element}
 * Returns the UI for associated accounts and popup view
 */

const ActiveInactiveAssociatePopup = ({
  flow,
  pan,
  ifsc,
  bank,
  accActiveInactiveState,
  setAccActiveInactiveState,
  currentAssociate,
  setCurrentAssociate,
  openAssociatePopup,
  setOpenAssociatePopup,
  dropDownOptions,
}) => {
  const handleDropdownChange = (val) => {
    setAccActiveInactiveState("active");
    setCurrentAssociate(val);
  };

  const handleCloseValue = () => {
    setAccActiveInactiveState("active");
    setCurrentAssociate(dropDownOptions[0]);
  };

  const [isalertPopup, setIsalertPopup] = useState(false);
  const [flowSelectedId, setFlowSelectedId] = useState("");
  const navigate = useNavigate();

  const fullviewhandleFunction = () => {
    if (currentAssociate === "Field force account") {
      navigate(
        `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page//${flowSelectedId}`,
      );
    } else if (currentAssociate === "Reimbursement account") {
      navigate(
        `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${flowSelectedId}`,
      );
    } else if (currentAssociate === "Tax account") {
      navigate(
        `/tax_account_dashboard/tax_account_view_all/tax_account_detail_page/${flowSelectedId}`,
      );
    } else {
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${flowSelectedId}`,
      );
    }
    setOpenAssociatePopup(false);
    setFlowSelectedId("");
  };

  return (
    <>
      {isalertPopup && (
        <div
          className={`fixed z-z-index-max flex  items-center justify-center  top-left-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity`}
        >
          <SmallPopup
            headerTitle={"Confirm leaving this page?"}
            headerFontWeight="semibold"
            headerFontColor="emphasis"
            textSize="font-lg"
            subtext={"Leaving this page will exit you from current flow."}
            btnOneText="Cancel"
            btnOneHandleFunction={() => setIsalertPopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText="Leave"
            btnTwoHandleFunction={() => {
              setIsalertPopup(false);
              fullviewhandleFunction();
            }}
            btnTwoEnabledBgColor="var(--text-primary)"
            btnTwoEnabledTextColor="var(--primary-bg)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoIsLoaderNeeded={true}
            btnTwoHoverOption="black"
          />
        </div>
      )}
      {openAssociatePopup && (
        <LargePopupRedirect
          closeiconhandleFunction={() => {
            setOpenAssociatePopup(false);
            setFlowSelectedId("");

            if (flow !== "Existing") {
              setAccActiveInactiveState("active");
              setCurrentAssociate("Field force account");
            }
          }}
          isActiveInactiveRequired={true}
          isDropDownRequired={true}
          dropDownValue={currentAssociate}
          handleDropdownChange={handleDropdownChange}
          isDropDownDisabled={flow === "Existing"}
          initialActiveTab={accActiveInactiveState}
          dropDownOptions={dropDownOptions}
          handleCloseValue={handleCloseValue}
          toggleActive={() => {
            setAccActiveInactiveState("active");
          }}
          toggleInactive={() => {
            setAccActiveInactiveState("inactive");
          }}
          redirectIcon={
            "https://q2pay.in/new_assets/common/settings/add_button_white.svg"
          }
          headerTitle={
            flow === "Existing" ? "Existing Accounts" : "Associated Accounts"
          }
          headersubtext={
            flow === "Existing"
              ? "The following existing accounts are linked to the provided PAN & bank across different account classification."
              : flow === "Pan"
                ? "The following associated accounts are linked to the provided PAN across different account classification."
                : "The following associated accounts are linked to the provided Bank across different account classification."
          }
        >
          {currentAssociate === "Field force account" ? (
            <FieldForceAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              activeInactiveFlow={flow}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
            />
          ) : currentAssociate === "Reimbursement account" ? (
            <ReimbursementAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveFlow={flow}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
            />
          ) : currentAssociate === "Tax account" ? (
            <TaxationAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveFlow={flow}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
            />
          ) : (
            <BeneficiaryAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveFlow={flow}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
            />
          )}
        </LargePopupRedirect>
      )}
      {flow === "Existing" ? (
        <div
          className="flex gap-gap-md justify-between items-center w-full rounded-border-radius-md bg-primary-card p-padding-md cursor-pointer global-hover-white z-z-index-content relative"
          onClick={() => {
            setOpenAssociatePopup(true);
          }}
        >
          <div className="flex flex-col gap-gap-sm w-width-full">
            <div className="w-full h-auto font-semibold text-base text-text-primary flex justify-between items-center">
              <h1>Existing Account Found</h1>

              <SVGComponent
                url={ICONS.ARROW_RIGHT_WHITE}
                height={"var(--icon-md)"}
                width={"var(--icon-md)"}
                color={"var(--text-primary)"}
              />
            </div>
            <ul className="w-full h-auto font-normal text-xs text-text-secondary list-disc pl-4  flex flex-col gap-gap-sm">
              <li>Click here to view and manage the account.</li>
            </ul>
          </div>
        </div>
      ) : flow === "notFoundPan" || flow === "notFoundBank" ? (
        <div className="flex gap-gap-md justify-between items-center w-full rounded-border-radius-md bg-primary-card p-padding-md ">
          <div className="flex flex-col gap-gap-xs w-width_full">
            <div className="w-full h-auto font-semibold text-base text-text-primary flex justify-between items-center">
              <h1>No Associate Accounts Found</h1>
            </div>
            <p className="w-full h-auto font-normal text-xs text-text-secondary list-disc   flex flex-col gap-gap-xs">
              {flow === "notFoundPan"
                ? "There are no existing accounts that are associated with the pan provided."
                : "There are no existing accounts that are associated with the bank provided."}
            </p>
          </div>
        </div>
      ) : (
        <div
          className="flex gap-gap-md justify-between items-center w-full rounded-border-radius-md bg-primary-card p-padding-md cursor-pointer global-hover-white z-z-index-content relative"
          onClick={() => {
            setOpenAssociatePopup(true);
          }}
        >
          <div className="flex flex-col gap-gap-sm w-width-full">
            <div className="w-full h-auto font-semibold text-base text-text-primary flex justify-between items-center">
              <h1>Associate Accounts Found</h1>

              <SVGComponent
                url={ICONS.ARROW_RIGHT_WHITE}
                height={"var(--icon-md)"}
                width={"var(--icon-md)"}
                color={"var(--text-primary)"}
              />
            </div>
            <ul className="w-full h-auto font-normal text-xs text-text-secondary list-disc pl-4  flex flex-col gap-gap-sm">
              {flow === "Pan" ? (
                <li>
                  This includes all active (approved, pending) and inactive
                  (rejected, archived, blocked) accounts linked to the provided
                  PAN.
                </li>
              ) : (
                <li>
                  This includes all active (approved, pending) and inactive
                  (rejected, archived, blocked) accounts linked to the provided
                  bank.
                </li>
              )}

              {flow === "Pan" ? (
                <li>
                  Click here to manage them, or continue updating the PAN
                  details.
                </li>
              ) : (
                <li>
                  Click here to manage them, or continue updating the bank
                  details.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default ActiveInactiveAssociatePopup;
